import React, { JSX, useState} from 'react';
import {
    Edit,
    TextInput,
    required,
    SelectArrayInput,
    BooleanInput,
    SelectInput,
    WithRecord,
    ArrayInput,
    SimpleFormIterator,
    TabbedForm,
    DateField,
    useGetIdentity,
    UseGetIdentityResult,
    ImageInput,
    ImageField,
    NumberField, NumberInput
} from 'react-admin';
import {
    Box,
    Stack,
    Grid, LinearProgress
} from '@mui/material';
import { nokMiniTagChoices, pricingTiers } from '../../defaults/defaults';
import { NokMini } from "../../types/entities";
import { MiniManagementContainer } from "./components/NokMini/MiniManagementContainer";
import { useASettingByCode } from "../global-hooks/default-system-hooks";
import { MiniConfigurationManagementContainer } from "./components/NokMini/MiniConfigurationManagementContainer";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";

export const NokMiniEdit: () => JSX.Element = (): JSX.Element => {
    const user: UseGetIdentityResult = useGetIdentity();
    const [miniName, setMiniName] = useState<string>('');
    const { loadingSetting, settingValue, loadSettingValue, error: settingError } = useASettingByCode("product_pricing_tiers");

    const selectChoices: { id: string, name: string }[] = [
        { id: 'disabled', name: 'Disabled' },
        { id: 'invitation', name: 'Invitation' },
        { id: 'available', name: 'Available' },
    ];
    const choices: { id: string, name: string }[] = nokMiniTagChoices;

    const developmentStatuses = [
        { id: 'alpha', name: 'Alpha' },
        { id: 'beta', name: 'Beta' },
        { id: '', name: 'Production' },
    ];

    const nokProcessingTypes: { id: string, name: string }[] = [
        { id: 'passive', name: 'Passive' },
        { id: 'active', name: 'Active' },
    ]

    function handleSetMiniName(name: string): void {
        setMiniName(name);
    }

    function sortSettingListOutput(): any {
        if (!loadingSetting) {
            const settingValues = JSON.parse(settingValue);
            let datum: { id: string; name: string }[] = [];
            for (const value of settingValues) {
                if (value.value === 'Personal Vault') {
                    continue;
                }
                datum.push({
                    id: value.value.toLowerCase().replaceAll(' ', '_'),
                    name: value.value
                });
            }
            return datum;
        }

        return [];
    }
    return (
        <Edit redirect={ false }>
            <TabbedForm>
                <TabbedForm.Tab label="General">
                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems:'center',
                        flexWrap: 'wrap',
                        marginBottom: '1rem'
                    }}>
                        <Box sx={{ width: '45%', display: 'flex', justifyContent: 'space-between', alignItems:'center' }}>
                            <span>{ "Last Updated:" }</span>
                            <DateField source="updatedAt" showTime/>
                        </Box>
                        <Box sx={{ width: '45%', display: 'flex', justifyContent: 'space-between', alignItems:'center' }}>
                            <span>{ "Created:" }</span>
                            <DateField source="createdAt" showTime/>
                        </Box>
                    </Box>
                    <Grid container spacing={ 2 } sx={ { width: '100%' } }>
                        <Grid size={6}>
                            <TextInput name="name" source="name" validate={[required()]} fullWidth />
                            <SelectInput source="status" choices={ selectChoices } />
                            <WithRecord label="" render={
                                (record: NokMini): JSX.Element => {
                                    if (!Array.isArray(record.tags)) {
                                        return null;
                                    }
                                    for (const item of record?.tags) {
                                        let tmp: { id: string, name: string } = {
                                            id: item.toLowerCase(),
                                            name: item,
                                        };
                                        choices.push(tmp);
                                    }

                                    return (
                                        <SelectArrayInput
                                            name="tags"
                                            source="tags"
                                            choices={ choices }
                                            onCreate={ (): { id: string, name: string } => {
                                                let newChoice: string = prompt("Enter new tag") ?? '';
                                                if (newChoice.length > 0) {
                                                    newChoice = newChoice.replaceAll(' ', '_').toLowerCase();
                                                    const newChoiceOption = {
                                                        id: newChoice.toLowerCase(),
                                                        name: newChoice,
                                                    };
                                                    choices.push(newChoiceOption);
                                                    return newChoiceOption;
                                                }
                                            } }
                                            createLabel="Create new option..."
                                        />
                                    );
                                }
                            } />
                            <ImageInput source="icon" label="Icon">
                                <ImageField source="src" title="title" />
                            </ImageInput>
                        </Grid>
                        <Grid size={ 6 }>
                            <NumberInput name="position" source="position"
                                         helperText="Force a position for this Mini in the Landing Page Toolbox"
                                         fullWidth />
                            <TextInput name="systemCode"
                                       source="systemCode"
                                       helperText="Be careful changing this value, it will be used in the system to identify this mini"
                                       validate={[required()]}
                                       fullWidth />
                            <NumberInput name="toolboxPosition" source="toolboxPosition"
                                         helperText="Force a position for this Mini in the Customer Portal Toolbox"
                                         fullWidth />
                            <SelectInput source="developmentStatus" choices={ developmentStatuses } />
                            <NumberInput name="creditsPerUse" source="creditsPerUse"
                                         helperText="Number of Credits per customer use of Mini or per target of each mini run"
                                         fullWidth />
                            <SelectInput source="nokProcessingType" choices={ nokProcessingTypes } helperText="Active processing type would mean the mini loads and processes Noks against a customer target value" />
                        </Grid>
                    </Grid>
                    {/* Inside a tab, not beside them: TabbedFormTabs clones every direct child into the
                        tab bar. One tab is enough - react-admin mounts them all and hides the
                        inactive ones, so the whole form is registered from here. */}
                    <ChatFieldBridge />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="CMS">
                    <Grid container
                          sx={ { width: '100%', margin: '0 0 40px 0', padding: '0 0 20px 0', borderBottom: '1px solid', borderBottomColor: 'divider' } }
                          spacing={ 2 }>
                        <Grid size={ 6 }>
                            <Stack spacing={ 2 } sx={ { width: '100%' } }>
                                <h3>{ "General" }</h3>
                                <BooleanInput source="hideOnLanding" helperText="Enabling will stop the Mini from rendering on the landing and customer portal pages" />
                                <TextInput name="landingUrlKey"
                                           source="landingUrlKey"
                                           validate={[required()]}
                                           label="URL Key"
                                           helperText="Must match the URL of the labding and customer portal pages or could cause 404"
                                           fullWidth />

                            </Stack>
                        </Grid>
                        <Grid size={ 6 }>
                            <Box sx={ {
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            } }>
                                <h5 style={ {
                                    textDecoration: "underline"
                                } } >
                                    { "Customer Vote Counter:" }
                                </h5>
                                <NumberField source="customerPortalVoteCounter" />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container spacing={ 2 }  sx={ { width: '100%' } }>
                        <Grid size={ 6 }>
                            <Stack spacing={ 2 }>
                                <h3>{ "Landing Pages" }</h3>
                                <h5 style={ {
                                   textDecoration: "underline"
                                } } >
                                    { "Mini Landing Page" }
                                </h5>
                                <TextInput name="landingDescription"
                                           source="landingDescription"
                                           fullWidth validate={[required()]}
                                           multiline
                                           rows={24}
                                           helperText="Description rendered at top of landing page"
                                />
                                <ArrayInput name="landingList"
                                            source="landingList"
                                            helperText="Appears under Landing Description on landing page and in the modal on 'invitation' tiles"
                                >
                                    <SimpleFormIterator inline>
                                        <TextInput source="item" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                                <ArrayInput name="questions" source="questions">
                                    <SimpleFormIterator inline>
                                        <TextInput source="question" />
                                        <TextInput source="answer" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                                <ArrayInput name="uspBlocks" source="uspBlocks" label="USP Blocks">
                                    <SimpleFormIterator inline>
                                        <TextInput source="title" />
                                        <TextInput source="content" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                                <ArrayInput name="howItWorksBlocks" source="howItWorksBlocks">
                                    <SimpleFormIterator inline>
                                        <TextInput source="title" />
                                        <TextInput source="content" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                                <h5 style={ {
                                    textDecoration: "underline"
                                } } >
                                    { "Features Landing Page" }
                                </h5>
                                <TextInput name="description"
                                           source="description"
                                           label="Features Grid Description"
                                           fullWidth
                                           validate={[required()]}
                                           multiline
                                           rows={24}
                                           helperText="This is used on the Features Landing page list" />

                            </Stack>
                        </Grid>
                        <Grid size={ 6 }>
                            <Stack spacing={ 2 }>
                                <h3>{ "Customer Portal" }</h3>
                                <TextInput name="customerPortalShortDescription"
                                           source="customerPortalShortDescription"
                                           fullWidth validate={[required()]}
                                           multiline
                                           rows={10}
                                           helperText="Customer Portal Short Descirption (100 characters)"
                                />
                                <TextInput name="customerPortalDescription"
                                           source="customerPortalDescription"
                                           fullWidth
                                           multiline
                                           rows={24}
                                           helperText="Customer Portal Descirption"
                                />
                                <ArrayInput name="customerPortalFeatureList" source="customerPortalFeatureList" label="Customer Portal Feature List">
                                    <SimpleFormIterator inline>
                                        <TextInput source="name" />
                                        <TextInput source="description" label="Short description" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                            </Stack>
                        </Grid>
                    </Grid>

                </TabbedForm.Tab>
                <TabbedForm.Tab label="Features">
                    <Stack spacing={2} sx={ { width: '100%' } }>
                        <h4 style={ { margin: '0 0 20px 0' } } >{ "Feature Tier Limits" }</h4>
                        <h5 style={ {
                            textDecoration: "underline"
                        } } >
                            { "Landing Page Price Block Comparison Data" }
                        </h5>
                        <ArrayInput name="landingPriceBlockTierComparisons"
                                    helperText="Use 'true' or 'false' to show Check or Cross icons. Enterprise Tier does not render"
                                    source="landingPriceBlockTierComparisons" label="Comparison Table Data">
                            <SimpleFormIterator inline>
                                <TextInput source="name" validate={[required()]} />
                                <TextInput source="helperText" label="Helper Text" />
                                <TextInput source="system_code" validate={[required()]} />
                                <ArrayInput source="features">
                                    <SimpleFormIterator>
                                        { (loadingSetting) &&
                                            <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                                        }
                                        { (!loadingSetting) &&
                                            <SelectInput source="tier" choices={ sortSettingListOutput() } />
                                        }
                                        <TextInput source="value" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                            </SimpleFormIterator>
                        </ArrayInput>
                    </Stack>
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Promotional">
                    <Grid container sx={ { width: '100%' } } spacing={ 2 }>
                        <Grid size={ 6 }>
                            <Stack spacing={2} sx={ { width: '100%' } }>
                                <h4 style={ { margin: '0 0 20px 0' } } >{ "Customer Portal Trident" }</h4>
                                <TextInput source="customerTridentTitle" label="Trident Title" />
                                <TextInput source="customerTridentCopy" label="Trident Description" multiline rows={ 5 } />
                                <SelectInput source="customerTridentPosition" label="Trident Position"
                                             choices={ [
                                                 { id: 1, name: 'Position 1' },
                                                 { id: 2, name: 'Position 2' },
                                                 { id: 3, name: 'Position 3' },
                                             ] }
                                             helperText={ "If more than one is set to a position, the first found will display" }
                                />
                                <ImageInput source="customerTridentImage"
                                            helperText="Approximate image dimensions: 560px X 350px"
                                            label="Trident Image">
                                    <ImageField source="src" title="title" />
                                </ImageInput>
                            </Stack>
                        </Grid>
                        <Grid size={ 6 }>
                            <Stack spacing={2} sx={ { width: '100%' } }>
                                <h4 style={ { margin: '0 0 20px 0' } } >{ "Landing Home Page Hero Triple List Item" }</h4>
                                <TextInput source="landingHeroTripleListTitle" label="Landing Hero Triple List Title" />
                                <TextInput source="landingHeroTripleListSummary" label="Landing Hero Triple List Summary" multiline rows={ 5 } />
                                <SelectInput source="landingHeroTripleListPosition" label="Landing Hero Triple Position"
                                             choices={ [
                                                 { id: 1, name: 'Position 1' },
                                                 { id: 2, name: 'Position 2' },
                                                 { id: 3, name: 'Position 3' },
                                             ] }
                                             helperText={ "If more than one is set to a position, the first found will display" }
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Subscription">
                    <Stack spacing={2} sx={ { width: '100%' } }>
                        <SelectInput source="pricingTier" choices={ pricingTiers } validate={[required()]} />
                    </Stack>
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Mini Manager">
                    <MiniConfigurationManagementContainer />
                </TabbedForm.Tab>
                <TabbedForm.Tab label={ (user?.data?.fullName ? user?.data?.fullName  + "'s " + miniName :  "'s Mini") }>
                    <MiniManagementContainer
                        handleSetMiniName={ handleSetMiniName }
                        miniName={ miniName }
                    />
                </TabbedForm.Tab>
            </TabbedForm>
        </Edit>
    );
}